import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    archUsable,
    createParam,
    getParam,
    getSchema,
    resolveSchema,
    listProcessArchs,
    listSchemas,
    ParamSchema,
    MaterialKeyKind,
    ParamRowPayload,
    ParamRowDetail,
    ProcessArch,
    RESULT_TAGS,
    submitParam,
    updateParam,
    validateParam,
    withdrawParam,
} from '../../api/parameter';
import { listDevices, deviceLabel, Device } from '../../api/device';
import { mmModeLabel, MMMode } from '../../api/mmode';
import { loadMaterialOptions } from '../../api/material';
import {
    categoryPairFallbackLabel,
    decodeCategoryPair,
    encodeCategoryPair,
    useCategoryPairOptions,
} from '../../utils/categoryOptions';
import SearchSelect from '../../components/SearchSelect';
import { withCurrent } from '../../utils/searchOptions';
import { extValueToInput, splitListInput } from '../device/deviceSchemaForm';
import ImageUploader from '../../components/ImageUploader';
import { ApiError, imgSrc } from '../../api/request';
import { FieldSpec, parseFields } from './SchemaEditModal';
import GroupedFields from '../../components/GroupedFields';
import { Layout, parseLayout, placeIntoLayout, stringifyLayout } from '../../utils/fieldGroups';
import PermissionLink from '../../components/PermissionLink';
import { fieldDisplayName } from '../../api/schemaField';
import { useSchemaFieldNames } from '../../utils/useSchemaFieldNames';
import { useDictText } from '../../components/DictSelect';
import { DictOptions, useDictOptions } from '../../utils/dictOptions';
import { planTypeAppliesTo } from '../../api/plantype';
import { usePlanTypes } from '../../utils/usePlanTypes';
import { STATUS_OPTS } from './paramArchSchemas';
import { useParamKeyOptions } from './useParamKeyOptions';
import { modeSelectOptions } from './ModeOptGroups';
import {
    NumInput,
    Select as ArchSelect,
    RequiredMark,
} from './paramArchRenderers';
import { confirmDialog, alertDialog, promptDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import { thumbUrl } from '../../utils/imgUrl';
import { visibleFieldKeys } from '../../utils/fieldVisibility';
import { useSchemaKeyLabel } from '../../utils/useSchemaKeyLabel';

// GroupedFields 的取值函数放模块级, 保持引用稳定 (组件内按它 memo 分组)
const fieldGroupAttrs = (f: FieldSpec) => f;
const fieldKeyOf = (f: FieldSpec) => f.k;

// 参数编辑页 (v4 多态分表版).
// URL ?arch=laser&id=<number> → 编辑模式: 拉行详情预填, 保存走 PATCH /v1/params/:arch/:id
// URL ?arch=laser (无 id)     → 新建模式, 保存走 POST /v1/params/:arch
//
// v4 起一行 = 一条参数记录: 页面就是"一条记录"的表单 —
// 材料键 (随框架分化) + 设备 + 加工类型 关键字段, 加一组 fields 驱动的 params 动态字段.

const RESULT_TAG_OPTS = RESULT_TAGS.map((r) => ({ value: r.v, label: r.label }));

const BOOL_OPTS = [
    { value: '是', label: '是' },
    { value: '否', label: '否' },
];

function SchemaInput({
    f,
    value,
    onChange,
    mmodes,
    dictOpts,
}: {
    f: FieldSpec;
    value: any;
    onChange: (v: any) => void;
    /** ★设计A: 加工模式值字典 (参-02 枚举值级中文显示) */
    mmodes?: MMMode[];
    /** 字典来源枚举 (f.os) 的候选值, 与设备 Schema 表单同一套 */
    dictOpts?: DictOptions;
}) {
    // 参-02: "加工模式"字段的枚举值经 mm_mode 字典显示中文, 存储仍是原值; 名称为空回退原值.
    if (f.k === 'processingMode' && mmodes && mmodes.length > 0) {
        const cur = String(value ?? '');
        return (
            <SearchSelect
                className="w-full"
                value={cur}
                onChange={(v) => onChange(v)}
                placeholder="未设置"
                options={withCurrent(
                    mmodes.map((m) => ({
                        value: m.value,
                        label: mmModeLabel(m),
                    })),
                    cur,
                    `${cur} (不在字典)`,
                )}
                clearable
            />
        );
    }
    // 枚举来源二选一 (与设备 Schema 一致): os=字典类目 → 拉字典显示中文; 否则用内联候选 o.
    if (f.t === 'enum' && f.os) {
        const opts = dictOpts?.[f.os] || [];
        const cur = String(value ?? '');
        return (
            <SearchSelect
                className="w-full"
                value={cur}
                onChange={(v) => onChange(v)}
                placeholder="未设置"
                options={withCurrent(
                    opts.map((o) => ({ value: o.code, label: o.label })),
                    cur,
                    `${cur} (不在字典)`,
                )}
                clearable
            />
        );
    }
    if (f.t === 'enum') {
        return (
            <ArchSelect
                value={String(value ?? '')}
                options={(f.o || []).map(String)}
                onChange={(v) => onChange(v)}
            />
        );
    }
    if (f.t === 'bool') {
        return (
            <SearchSelect
                className="w-full"
                value={value ? '是' : '否'}
                onChange={(v) => onChange(v === '是')}
                options={BOOL_OPTS}
            />
        );
    }
    if (f.t === 'array') {
        return <ListInput value={value} onChange={onChange} />;
    }
    if (f.t === 'num' || f.t === 'int') {
        return (
            <NumInput
                value={value}
                unit={f.u}
                step={f.t === 'int' ? 1 : undefined}
                onChange={onChange}
            />
        );
    }
    return (
        <input
            className="inp"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

// ListInput 多值字段 (t=array, 如 FDM 机器限制 [正常, 静音]): 逗号分隔录入, 失焦时转成数组;
// 能转数字的项存数字, 其余存原文. 本地保留输入文本, 免得边输边规整吃掉末尾的逗号.
function ListInput({
    value,
    onChange,
}: {
    value: unknown;
    onChange: (v: unknown) => void;
}) {
    const [text, setText] = useState(extValueToInput(value));
    useEffect(() => setText(extValueToInput(value)), [value]);
    return (
        <input
            className="inp"
            value={text}
            placeholder="多个值用逗号分隔"
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
                const list = splitListInput(text).map((x) => {
                    const n = Number(x);
                    return Number.isFinite(n) ? n : x;
                });
                onChange(list.length ? list : undefined);
            }}
        />
    );
}

// mergeWithTemplate 详情/复制模式的字段集来源 (参-04):
// 字段定义只认「Schema 模板」(按行键最特异匹配, 后端 detail.schema 给出) —
// 行自身的 fields 只是历史快照, 只在两种情况下补位:
//   1. 没命中任何模板 (老数据/模板被删) → 整体退回行快照;
//   2. 快照里有值、但模板已不含的字段 → 追加到末尾, 避免存量值在表单里看不见也改不掉.
function mergeWithTemplate(
    tplFields: FieldSpec[],
    rowFields: FieldSpec[],
    params: Record<string, unknown>,
): { fields: FieldSpec[]; extraKeys: string[] } {
    if (tplFields.length === 0) return { fields: rowFields, extraKeys: [] };
    const inTpl = new Set(tplFields.map((f) => f.k));
    const extras = rowFields.filter(
        (f) =>
            !inTpl.has(f.k) &&
            !(
                params[f.k] === undefined ||
                params[f.k] === null ||
                params[f.k] === ''
            ),
    );
    return {
        fields: [...tplFields, ...extras],
        extraKeys: extras.map((f) => f.k),
    };
}

function defaultParams(fields: FieldSpec[]): Record<string, unknown> {
    const defaults: Record<string, unknown> = {};
    for (const f of fields) if (f.v !== undefined) defaults[f.k] = f.v;
    return defaults;
}

// KEY_FIELD_KEYS 已提升到「关键字段」区单独渲染的动态字段, 参数值区不再重复渲染.
// processingMode (加工模式): 它决定模板键的 groupCode 段 —— 是关键字段而不是普通参数值,
// 留在动态区会形成 "选不到模式 → 命不中带模式的模板 → 表单里根本没有这个字段" 的死循环.
// planType (方案类型) 同理提到关键字段区: 下拉选项按当前工艺架构取自字典 plan_type (scoped).
const KEY_FIELD_KEYS = new Set(['processingMode', 'planType']);

// keySigOf 决定"这行吃哪张模板"的四个关键字段的签名 (变了才重解析模板).
function keySigOf(
    archCode: string,
    deviceKey: string,
    subProcess: string,
    modeValue: string,
): string {
    return [archCode, deviceKey, subProcess, modeValue].join('|');
}

// applyTemplateDefaults 换模板后把新字段的默认值补进已有参数值 (已填的值一律不覆盖).
function applyTemplateDefaults(
    cur: Record<string, unknown>,
    tplFields: FieldSpec[],
): Record<string, unknown> {
    let changed = false;
    const next = { ...cur };
    for (const f of tplFields) {
        if (f.v === undefined) continue;
        if (next[f.k] === undefined || next[f.k] === '') {
            next[f.k] = f.v;
            changed = true;
        }
    }
    return changed ? next : cur;
}

function ParamManage() {
    const canUpdate = useCan('param_row', 'update');
    const canSeeDevice = useCan('device', 'get');
    const location = useLocation();
    const history = useHistory();
    const query = new URLSearchParams(location.search);
    const editingIDRaw = query.get('id') || '';
    const editingID = editingIDRaw ? Number(editingIDRaw) : 0;
    const presetArchCode = query.get('arch') || query.get('archCode') || '';
    const isEditing = editingID > 0;
    // 参-01 (2026-08-05): 从"详情"进入默认只读查看, 点"编辑"才解锁表单, 防误改 (尤其 published 行)
    const [viewMode, setViewMode] = useState(editingID > 0);
    // RBAC (2026-09-12): 没有参数编辑权的人只能看 —— 编辑态 (含新建空表单) 一律切回只读, 页面上所有写控件随之消失.
    useEffect(() => {
        if (!canUpdate && !viewMode) setViewMode(true);
    }, [canUpdate, viewMode]);
    // 取消编辑时 +1 触发行详情重拉, 丢弃未保存改动后退回只读
    const [reloadTick, setReloadTick] = useState(0);
    // ?copyFrom=<id>: 新建模式下以某行为模板预填 (材料键/设备/加工类型/fields/params), 高频录入相邻档位用
    const copyFromID = !isEditing ? Number(query.get('copyFrom') || 0) : 0;

    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [archCode, setArchCode] = useState<string>(presetArchCode);
    const [devices, setDevices] = useState<Device[]>([]);
    const [fields, setFields] = useState<FieldSpec[]>([]);
    // 分组布局 (2026-10-23): 只取命中模板的 layout (一切以模板为准); null = 平铺
    const [layout, setLayout] = useState<Layout>(null);
    const [schemaError, setSchemaError] = useState('');
    // 框架没有默认模板 (全空键, 如 FDM 模板都按机型绑定): 不是配置错误, 选好关键字段后按命中的模板出字段
    const [noDefaultTpl, setNoDefaultTpl] = useState(false);
    // 命中的模板键 (详情页说明"字段来自哪张模板", 可跳模板管理) + 模板已不含但仍有值的字段 (快照补位)
    const [schemaKeyHit, setSchemaKeyHit] = useState<
        ParamRowDetail['schemaKey'] | null
    >(null);
    const [extraFieldKeys, setExtraFieldKeys] = useState<string[]>([]);
    // 模板键展示名与模板管理页同一份 (加工模式/加工类型取字典中文名, 不露 code)
    const { keyLabel: schemaKeyLabel } = useSchemaKeyLabel(archCode);
    const schemaFrom = schemaKeyHit ? schemaKeyLabel(schemaKeyHit) : '';
    // 跳 Schema模板管理 并定位到命中的那张模板 (无命中就只切到本框架)
    const schemaLink = `/param-sets/templates?${new URLSearchParams({
        arch: archCode,
        // 存量未拆分模板 (无设备段) 定位到「存量按机型 (待拆分)」筛选
        deviceKey:
            schemaKeyHit?.deviceKey || (schemaKeyHit?.family ? 'ANY' : ''),
        group: schemaKeyHit?.groupCode || '',
        processType: schemaKeyHit?.processType || '',
    }).toString()}`;
    // 字典来源枚举 (字段 os) 的候选值; 与设备 Schema 表单共用取数层
    const dictOpts = useDictOptions(
        fields.map((f) => f.os),
        archCode,
    );
    // 字段显示名兜底 (Schema 字段字典): 行级 label > 字典名称 > key
    const fieldNames = useSchemaFieldNames();
    // 设备展示名的光源段走字典 label (light_source), 不裸显 code
    const lightText = useDictText('light_source');
    // ★设计A: 加工模式值字典 (参-02 值级中文), 模块级缓存全站一次请求
    // 本框架全部字段模板 (加工模式候选的来源: 只有建了模板的模式组才可选)
    const [archSchemas, setArchSchemas] = useState<ParamSchema[]>([]);

    // 关键字段: 材料键 (三组按框架粒度三选一) + 设备 + 加工类型
    const [xcode, setXcode] = useState('');
    const [xcodeLabel, setXcodeLabel] = useState('');
    const [filmXcode, setFilmXcode] = useState('');
    const [filmLabel, setFilmLabel] = useState('');
    const [substrateXcode, setSubstrateXcode] = useState('');
    const [substrateLabel, setSubstrateLabel] = useState('');
    const [categoryCode, setCategoryCode] = useState('');
    const [categoryType1, setCategoryType1] = useState('');
    const [deviceKey, setDeviceKey] = useState('');
    const [subProcess, setSubProcess] = useState('');
    const [status, setStatus] = useState('draft');
    // 结果标 (实测结论). 与参数值同属一行, 改它同样回落 draft → 提交评审 → 管理员审批.
    // 结果标必填 (2026-08-27 废止 pending): 空 = 还没选, 提交前由 validate 拦下
    const [resultTag, setResultTag] = useState('');
    const [legacyMaterialId, setLegacyMaterialId] = useState(0);
    const [sourceTable, setSourceTable] = useState('');
    // extra 原样带回防丢失 (迁移溯源等)
    const [extraRaw, setExtraRaw] = useState('');
    // 跳转到其它参数行的 search box

    // 参数值 (fields 驱动的动态表单; 一行一条记录)
    const [params, setParams] = useState<Record<string, unknown>>({});


    const [busy, setBusy] = useState(false);
    const [pageError, setPageError] = useState('');
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [dirty, setDirty] = useState(false);
    // 效果图/矩阵 (2026-07-30 起内嵌参数行, 与源 material_process_para 同名同义)
    const [effectImage, setEffectImage] = useState('');
    const [supportMatrix, setSupportMatrix] = useState(false);
    const [paraMatrix, setParaMatrix] = useState(''); // 矩阵 JSON 原样带回, 页面只读展示
    // 仅对当前加工模式生效 (覆盖行): 同组其它模式仍用共用行; 如某块打印板不能用 = 该板一行「不支持」
    const [modeOnly, setModeOnly] = useState(false);

    const activeArch = archs.find((a) => a.code === archCode);
    const keyKind: MaterialKeyKind = activeArch?.materialKeyKind || 'xcode';
    // inkjet: 类别候选 = (一级, 二级) 成对, 显示「一级 / 二级」
    const catPair = useCategoryPairOptions(keyKind === 'category_code');
    const categoryValue = encodeCategoryPair(categoryType1, categoryCode);

    // 加工模式值: 存在 params.processingMode (后端 mmGroupCodeFor 也认这个键), 提到关键字段区选.
    const processingMode = String(params.processingMode ?? '');
    const setProcessingMode = (v: string) => {
        setParams((p) => ({ ...p, processingMode: v }));
        setDirty(true);
    };
    // 方案类型: 存在 params.planType (选填); 清空时删键, 不落空串
    const planType = String(params.planType ?? '');
    // 选项 = 当前架构下启用的方案类型 (独立表 plan_type), 再按所选设备过滤:
    // 绑了设备的条目只在这些设备上出现, 没绑的全设备可选
    const planTypeAll = usePlanTypes(archCode);
    const planTypeOpts = planTypeAll.filter((o) => planTypeAppliesTo(o, deviceKey));
    const setPlanType = (v: string) => {
        setParams((p) => {
            const next = { ...p };
            if (v === '') delete next.planType;
            else next.planType = v;
            return next;
        });
        setDirty(true);
    };
    // 模板按设备建 (2026-11-01), 录入页不再选机型; 机型由所选设备带出, 只给存量未拆分模板过渡期命中用
    const family =
        devices.find((d) => d.deviceKey === deviceKey)?.family || '';
    // 加工模式候选来源于「参数模板」: 所选设备已建模板的模式组对应的
    // 加工模式 / 加工类型候选 (只列建了模板的), 与参数批量导入共用同一口径
    const {
        mmodes,
        modeGroups,
        modeLabel,
        groupCodeOf,
        subProcessOpts,
        subProcessShownOpts,
    } = useParamKeyOptions({
        archCode,
        family,
        deviceKey,
        processingMode,
        archSchemas,
    });

    // 关键字段下拉候选 (可搜索下拉, 与参数列表筛选同款)
    const deviceOpts = useMemo(
        () =>
            devices.map((d) => ({
                value: d.deviceKey,
                label: deviceLabel(d, lightText),
            })),
        [devices, lightText],
    );
    const modeSelectOpts = useMemo(
        () => modeSelectOptions(modeGroups, modeLabel),
        [modeGroups, modeLabel],
    );

    // 模板解析用的最新快照 (解析回调里合并字段/补默认值时读, 不进 effect 依赖避免自触发)
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;
    const paramsRef = useRef(params);
    paramsRef.current = params;
    // 关键字段基线签名: 编辑/复制模式等行详情回填完才建立, 之后用户改了键才重解析模板.
    const baselineKeySig = useRef<string | null>(
        isEditing || copyFromID > 0 ? null : '',
    );

    // 矩阵元数据: paraMatrix JSON 解析失败按无矩阵处理 (只读展示用)
    const matrixMeta = useMemo(() => {
        if (!paraMatrix) return null;
        try {
            const o = JSON.parse(paraMatrix) as Record<string, unknown>;
            if (!o || typeof o !== 'object') return null;
            return {
                frontImg: typeof o.frontImg === 'string' ? o.frontImg : '',
                row: o.row as number | undefined,
                col: o.col as number | undefined,
                xType: o.xType as string | undefined,
                yType: o.yType as string | undefined,
                minPower: o.minPower as number | undefined,
                maxPower: o.maxPower as number | undefined,
                minSpeed: o.minSpeed as number | undefined,
                maxSpeed: o.maxSpeed as number | undefined,
                matrixPoint: Array.isArray(o.matrixPoint)
                    ? o.matrixPoint
                    : undefined,
            };
        } catch {
            return null;
        }
    }, [paraMatrix]);

    // setMatrixFrontImg 只替换矩阵 JSON 里的整图地址 (frontImg), 行列/坐标等其余字段原样保留.
    const setMatrixFrontImg = (url: string) => {
        try {
            const o = (paraMatrix ? JSON.parse(paraMatrix) : {}) as Record<
                string,
                unknown
            >;
            o.frontImg = url;
            setParaMatrix(JSON.stringify(o));
            setDirty(true);
        } catch {
            // JSON 损坏时不动, 避免把整个矩阵数据覆写掉
        }
    };

    // 1. 启动: 拉框架注册表
    useEffect(() => {
        listProcessArchs()
            .then((list) => {
                setArchs(list);
                if (!isEditing && !archCode) {
                    const first = list.find(archUsable);
                    setArchCode(presetArchCode || (first?.code ?? ''));
                }
            })
            .catch(() => setArchs([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 本框架模板清单 (加工模式候选的来源): 只读态用不到 —— 行自身的字段集由后端 detail.schema 给,
    // 这里的清单只服务于编辑时切"加工模式"下拉. 无编辑权就不拉, 只读角色因此不需要 param_schema 读权 (2026-09-13).
    useEffect(() => {
        if (!archCode || !canUpdate) {
            setArchSchemas([]);
            return;
        }
        listSchemas(archCode)
            .then(setArchSchemas)
            .catch(() => setArchSchemas([]));
    }, [archCode, canUpdate]);

    // 2. 编辑模式: 拉参数行详情 → 预填
    useEffect(() => {
        if (!isEditing || !presetArchCode) return;
        setLoadingDetail(true);
        setPageError('');
        getParam(presetArchCode, editingID)
            .then((d) => {
                setArchCode(presetArchCode);
                setXcode(d.xcode || '');
                setXcodeLabel('');
                setFilmXcode(d.filmXcode || '');
                setFilmLabel('');
                setSubstrateXcode(d.substrateXcode || '');
                setSubstrateLabel('');
                setCategoryCode(d.type2 || '');
                setCategoryType1(d.type1 || '');
                setDeviceKey(d.deviceKey);
                setSubProcess(d.subProcess || '');
                setStatus(d.status);
                setResultTag(d.resultTag || '');
                setLegacyMaterialId(d.legacyMaterialId || 0);
                setSourceTable(d.sourceTable || '');
                setExtraRaw(d.extra || '');
                setEffectImage(d.effectImage || '');
                setModeOnly(!!d.overrideModeValue);
                setSupportMatrix(!!d.supportMatrix);
                setParaMatrix(
                    d.paraMatrix && d.paraMatrix !== 'null' ? d.paraMatrix : '',
                );
                let obj: Record<string, unknown> = {};
                try {
                    const parsed = d.params
                        ? (JSON.parse(d.params) as Record<string, unknown>)
                        : {};
                    obj = parsed && typeof parsed === 'object' ? parsed : {};
                } catch {
                    obj = {};
                }
                setParams(obj);
                // 参-04: 字段定义来源 Schema 模板 (后端按本行键最特异匹配); 行 fields 只补位
                const merged = mergeWithTemplate(
                    parseFields(d.schema?.fields || '[]'),
                    parseFields(d.fields || '[]'),
                    obj,
                );
                setFields(merged.fields);
                setLayout(parseLayout(d.schema?.layout)); // 布局一切以模板为准, 模板没分组就平铺
                setSchemaKeyHit(d.schemaKey || null);
                setExtraFieldKeys(merged.extraKeys);
                // 这行现在的关键字段 = 基线; 用户改了键之后才按新键重解析模板
                baselineKeySig.current = keySigOf(
                    presetArchCode,
                    d.deviceKey,
                    d.subProcess || '',
                    String(obj.processingMode ?? ''),
                );
            })
            .catch((e) => {
                const isNotFound =
                    e instanceof ApiError &&
                    (e.message?.includes('不存在') ||
                        e.message?.includes('not found'));
                if (isNotFound) {
                    setPageError(
                        `参数行 #${editingID} 不存在 (可能已被删除或链接已过期). 请回参数总览刷新.`,
                    );
                } else {
                    const msg =
                        e instanceof ApiError
                            ? `${e.code}: ${e.message}`
                            : String(e);
                    setPageError(`加载参数行失败: ${msg}`);
                }
            })
            .finally(() => setLoadingDetail(false));
    }, [isEditing, editingID, presetArchCode, reloadTick]);

    // 编辑模式但 URL 缺 arch: 无法路由到参数表, 给出明确指引
    useEffect(() => {
        if (isEditing && !presetArchCode) {
            setPageError(
                '链接缺少工艺框架参数 (?arch=), 请从参数总览重新进入.',
            );
        }
    }, [isEditing, presetArchCode]);

    // 复制新建: 以 copyFrom 行为模板预填关键字段 + fields + params.
    // 不复制迁移溯源 (legacy/source/extra) 与状态 — 新行从 draft 开始.
    useEffect(() => {
        if (!copyFromID || !presetArchCode) return;
        setLoadingDetail(true);
        getParam(presetArchCode, copyFromID)
            .then((d) => {
                setArchCode(presetArchCode);
                setXcode(d.xcode || '');
                setFilmXcode(d.filmXcode || '');
                setSubstrateXcode(d.substrateXcode || '');
                setCategoryCode(d.type2 || '');
                setCategoryType1(d.type1 || '');
                setDeviceKey(d.deviceKey);
                setSubProcess(d.subProcess || '');
                setStatus('draft');
                setLegacyMaterialId(0);
                setSourceTable('');
                setExtraRaw('');
                setEffectImage('');
                setSupportMatrix(false);
                setParaMatrix('');
                let obj: Record<string, unknown> = {};
                try {
                    const parsed = d.params
                        ? (JSON.parse(d.params) as Record<string, unknown>)
                        : {};
                    obj = parsed && typeof parsed === 'object' ? parsed : {};
                } catch {
                    obj = {};
                }
                setParams(obj);
                const merged = mergeWithTemplate(
                    parseFields(d.schema?.fields || '[]'),
                    parseFields(d.fields || '[]'),
                    obj,
                );
                setFields(merged.fields);
                setLayout(parseLayout(d.schema?.layout)); // 布局一切以模板为准, 模板没分组就平铺
                setExtraFieldKeys(merged.extraKeys);
                baselineKeySig.current = keySigOf(
                    presetArchCode,
                    d.deviceKey,
                    d.subProcess || '',
                    String(obj.processingMode ?? ''),
                );
                setDirty(true); // 提醒用户这是未保存的新行
            })
            .catch((e) => {
                const msg =
                    e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e);
                setPageError(`复制模板加载失败: ${msg}`);
            })
            .finally(() => setLoadingDetail(false));
    }, [copyFromID, presetArchCode]);

    // 3. 切框架: 拉设备; 新建模式再从框架模板拷默认字段
    const loadForArch = useCallback(
        async (code: string, resetData: boolean) => {
            if (!code) return;
            setSchemaError('');
            setNoDefaultTpl(false);
            try {
                // 设备下拉只在有设备查看权时拉 (2026-09-13): 参数只读角色没有 device get,
                // 只读态也不需要选设备 —— 列表里按 deviceKey 原样显示.
                const devRes = canSeeDevice
                    ? await listDevices({ archCode: code, pageSize: 100 })
                    : { list: [] as Device[] };
                setDevices(devRes.list || []);
                // 参-03: 必填项不预填默认值 (与"材料"一致), 由用户显式选择
                if (resetData) {
                    setDeviceKey('');
                }
            } catch {
                setDevices([]);
            }
            if (!resetData) return; // 编辑/复制模式: 字段已由行详情回填
            try {
                const s = await getSchema(code);
                const list = parseFields(s.fields);
                setFields(list);
                setLayout(parseLayout(s.layout));
                setParams(defaultParams(list));
            } catch (e) {
                if (e instanceof ApiError && e.code === 10003) {
                    setFields([]);
                    setLayout(null);
                    setParams({});
                    setNoDefaultTpl(true);
                } else {
                    const msg =
                        e instanceof ApiError
                            ? `${e.code}: ${e.message}`
                            : String(e);
                    setSchemaError(msg);
                    setFields([]);
                    setParams({});
                }
            }
        },
        [canSeeDevice],
    );

    useEffect(() => {
        if (!archCode) return;
        loadForArch(archCode, !isEditing && !copyFromID);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archCode]);

    // 参-04: 关键字段 (设备 / 加工模式 / 加工类型) 一变就按模板键最特异匹配重取字段集 —
    // 字段只能来源模板 (管理员管理 → Schema模板管理 维护), 已填参数值保留.
    // groupCode 由加工模式值经 mm_mode 字典换算, 与后端写路径 (service.mmGroupCodeFor) 同一口径,
    // 所以表单看到的字段集 = 保存后这行真正命中的模板.
    const keySig = keySigOf(archCode, deviceKey, subProcess, processingMode);
    useEffect(() => {
        // 编辑/复制模式的首屏字段集由行详情给出 (含行快照补位), 键没被改就不重解析
        if (
            !archCode ||
            baselineKeySig.current === null ||
            keySig === baselineKeySig.current
        )
            return;
        // 关键字段全空 = 只能命中框架默认模板, 那份由切框架时的 loadForArch 加载; 这里再解析一次是重复请求,
        // 框架没有默认模板 (如 FDM) 时还会多报一条"未命中" (此时 getSchema 尚未返回, 无从预知)
        if (!deviceKey && !subProcess && !processingMode) return;
        const groupCode = groupCodeOf(processingMode);
        let alive = true;
        resolveSchema(
            archCode,
            { family, groupCode, processType: subProcess },
            deviceKey,
        )
            .then((tpl) => {
                if (!alive) return;
                const tplFields = parseFields(tpl.fields);
                // 模板已不含、但当前行仍有值的字段追加在末尾 (与详情页同一套补位规则)
                const merged = mergeWithTemplate(
                    tplFields,
                    fieldsRef.current,
                    paramsRef.current,
                );
                setFields(merged.fields);
                setLayout(parseLayout(tpl.layout));
                setExtraFieldKeys(merged.extraKeys);
                setParams(applyTemplateDefaults(paramsRef.current, tplFields));
                setSchemaKeyHit({
                    archCode: tpl.archCode,
                    deviceKey: tpl.deviceKey,
                    family: tpl.family,
                    groupCode: tpl.groupCode,
                    processType: tpl.processType,
                });
                setSchemaError('');
            })
            .catch(() => {
                // 无模板命中 (含框架默认缺失): 维持上一份字段集, 但要说清楚为什么没跟着变
                if (!alive) return;
                setSchemaKeyHit(null);
                setSchemaError(
                    '当前键 (设备 / 加工模式 / 加工类型) 未命中任何 Schema 模板, 字段集沿用上一份. 去 管理员管理→Schema模板管理 给该设备补模板.',
                );
            });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keySig, devices, mmodes]);

    const onSetArch = (code: string) => {
        if (isEditing) return; // 编辑模式锁定框架 (跨表改不成立)
        setArchCode(code);
        setXcode('');
        setFilmXcode('');
        setSubstrateXcode('');
        setCategoryCode('');
        setSubProcess('');
    };

    const validate = (): string => {
        if (!archCode) return '请选择工艺框架';
        if (!deviceKey) return '请选择设备';
        if (keyKind === 'xcode' && !xcode) return '请选择材料';
        if (keyKind === 'dual_xcode' && !filmXcode) return '请选择膜 (主材料)';
        if (keyKind === 'category_code' && (!categoryCode || !categoryType1))
            return '请选择材料分类 (一级 / 二级)';
        if (!resultTag) return '请选择结果 (✅推荐 / ❌不支持)';
        if (modeOnly && !processingMode)
            return '「仅对此加工模式生效」需要先选加工模式';
        return '';
    };

    // persistEdits 把编辑器里的改动落盘 (关键字段 + 字段定义 + 参数值).
    // PATCH 后端会强制 status=draft, 所以这是"提交评审"的前置. 返回参数行 id.
    const persistEdits = async (): Promise<number> => {
        const msg = validate();
        if (msg) throw new Error(msg);
        // 有布局时模板外字段 (行快照里有、模板已删) 收进末尾「未归组」, 保证字段都落在布局里、值不丢
        const placed = placeIntoLayout(fields, layout);
        const payload: ParamRowPayload = {
            deviceKey,
            subProcess: subProcess || undefined,
            fields: JSON.stringify(placed.fields),
            layout: stringifyLayout(placed.layout),
            params: JSON.stringify(params),
            effectImage: effectImage.trim(),
            supportMatrix,
            paraMatrix: paraMatrix || '',
            resultTag,
            overrideModeValue: modeOnly ? processingMode : '',
        };
        if (keyKind === 'xcode') payload.xcode = xcode;
        if (keyKind === 'dual_xcode') {
            payload.filmXcode = filmXcode;
            payload.substrateXcode = substrateXcode;
        }
        if (keyKind === 'category_code') {
            payload.type2 = categoryCode;
            payload.type1 = categoryType1;
        }
        let targetID = editingID;
        if (isEditing) {
            await updateParam(archCode, editingID, payload);
        } else {
            const res = await createParam(archCode, {
                ...payload,
                deviceKey,
                extra: extraRaw || undefined,
            });
            targetID = res.id;
        }
        setDirty(false);
        setStatus('draft');
        return targetID;
    };

    const manageURL = (id: number) =>
        `/param-sets/manage?arch=${encodeURIComponent(archCode)}&id=${id}`;

    const onSaveDraft = async () => {
        setPageError('');
        setBusy(true);
        try {
            const targetID = await persistEdits();
            if (!isEditing) {
                history.push(manageURL(targetID));
            } else {
                alertDialog(`已保存草稿 #${targetID}`);
            }
        } catch (e) {
            const m =
                e instanceof ApiError
                    ? `${e.code}: ${e.message}`
                    : (e as Error).message;
            setPageError(m);
        } finally {
            setBusy(false);
        }
    };

    // 保存并复制新建: 落盘当前行, 然后跳到 ?copyFrom=<id> 的新建页 (关键字段+字段+数值带过去).
    const onSaveAndCopy = async () => {
        setPageError('');
        setBusy(true);
        try {
            const targetID = await persistEdits();
            history.push(
                `/param-sets/manage?arch=${encodeURIComponent(archCode)}&copyFrom=${targetID}`,
            );
        } catch (e) {
            const m =
                e instanceof ApiError
                    ? `${e.code}: ${e.message}`
                    : (e as Error).message;
            setPageError(m);
        } finally {
            setBusy(false);
        }
    };

    const onValidateRange = async () => {
        if (!isEditing) {
            setPageError('请先保存草稿后再校验');
            return;
        }
        setBusy(true);
        setPageError('');
        try {
            const res = await validateParam(archCode, editingID);
            if (res.pass) {
                alertDialog('校验通过');
                return;
            }
            alertDialog(`校验未通过:\n${(res.issues || []).join('\n')}`);
        } catch (e) {
            const m =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setPageError(`校验失败: ${m}`);
        } finally {
            setBusy(false);
        }
    };

    // 提交评审: 先把本地改动落盘 (published 等状态会回落 draft), 再 submit.
    const onSubmitReview = async () => {
        const summary =
            (await promptDialog('提交说明 (可选, 给审批人看):', '')) || '';
        setBusy(true);
        setPageError('');
        try {
            const id = await persistEdits();
            const r = await submitParam(archCode, id, summary);
            setStatus('reviewing');
            if (!isEditing) {
                history.push(manageURL(id));
            }
            alertDialog(
                `已提交评审\n任务编号: ${r.taskCode}\n现在管理员可在「审批中心」处理`,
            );
        } catch (e) {
            const m =
                e instanceof ApiError
                    ? `${e.code}: ${e.message}`
                    : (e as Error).message;
            setPageError(`提交失败: ${m}`);
        } finally {
            setBusy(false);
        }
    };

    const onWithdraw = async () => {
        if (!(await confirmDialog('确认撤回该参数行的评审?'))) return;
        setBusy(true);
        setPageError('');
        try {
            await withdrawParam(archCode, editingID);
            setStatus('draft');
        } catch (e) {
            const m =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setPageError(`撤回失败: ${m}`);
        } finally {
            setBusy(false);
        }
    };

    const archIsLocked = isEditing;
    const formLocked = isEditing && status === 'reviewing';
    const statusLockReason =
        status === 'reviewing'
            ? '该参数行正在审批中, 等审批人通过/驳回后再修改'
            : '';

    if (loadingDetail) {
        return <div className="text-sm text-slate-500">加载参数行…</div>;
    }

    // 材料键横幅摘要 (编辑模式)
    const materialKeySummary =
        keyKind === 'dual_xcode'
            ? `${filmLabel || filmXcode || '—'}${substrateXcode ? ` + ${substrateLabel || substrateXcode}` : ''}`
            : keyKind === 'category_code'
              ? catPair.text(categoryType1, categoryCode) || '—'
              : xcodeLabel || xcode || '—';

    // 关键字段区已经渲染过的字段 (加工模式) 不在参数值区重复出一遍
    const formFields = fields.filter((f) => !KEY_FIELD_KEYS.has(f.k));
    // 字段联动: 开关未开启 / 枚举不匹配的字段不展示 (值原样保留), 规则见 utils/fieldVisibility
    const visKeys = visibleFieldKeys(
        fields.map((f) => f.k),
        params,
    );
    const linkedFields = formFields.filter((f) => visKeys.has(f.k));
    const linkHiddenCnt = formFields.length - linkedFields.length;

    return (
        <div className="space-y-5">
            {/* 编辑模式横幅 */}
            {isEditing && (
                <div
                    className="card p-3 flex items-center gap-3"
                    style={{ background: '#eef4ff', borderColor: '#bcd0ff' }}
                >
                    <span className="text-sm">
                        {viewMode ? '查看参数行' : '正在编辑参数行'}{' '}
                        <b className="font-mono">
                            {archCode}#{editingID}
                        </b>
                        {viewMode && (
                            <span className="ml-2 tag bg-slate-100 text-slate-500">
                                只读
                            </span>
                        )}
                    </span>
                    <span className="text-xs text-slate-500">
                        {materialKeySummary} ·{' '}
                        {(() => {
                            const dev = devices.find(
                                (d) => d.deviceKey === deviceKey,
                            );
                            return dev
                                ? deviceLabel(dev, lightText)
                                : deviceKey || '—';
                        })()}{' '}
                        ·{' '}
                        <span
                            className={`tag ${
                                status === 'published'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : status === 'reviewing'
                                      ? 'bg-amber-50 text-amber-700'
                                      : status === 'rejected'
                                        ? 'bg-rose-50 text-rose-700'
                                        : 'bg-slate-100 text-slate-600'
                            }`}
                        >
                            {status}
                        </span>
                    </span>
                    {viewMode ? (
                        <button
                            type="button"
                            className="ml-auto btn-primary text-xs"
                            onClick={() => setViewMode(false)}
                            hidden={!canUpdate}
                        >
                            ✎ 编辑
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="ml-auto btn-ghost text-xs"
                            onClick={async () => {
                                if (
                                    dirty &&
                                    !(await confirmDialog(
                                        '放弃未保存的修改, 退回只读查看?',
                                    ))
                                )
                                    return;
                                setViewMode(true);
                                setDirty(false);
                                setReloadTick((t) => t + 1);
                            }}
                        >
                            取消编辑
                        </button>
                    )}
                    <PermissionLink
                        resource="param_row"
                        hide
                        to="/param-sets"
                        className="text-sm text-brand-600 hover:underline"
                    >
                        ← 返回参数总览
                    </PermissionLink>
                </div>
            )}

            {/* 参-03 (2026-08-05): 新建模式的标题态, 与编辑横幅对称 */}
            {!isEditing && (
                <div
                    className="card p-3 flex items-center gap-3"
                    style={{ background: '#effdf4', borderColor: '#bbe8cd' }}
                >
                    <span className="text-sm">
                        新建参数行{' '}
                        <b className="font-mono">{archCode || '(选择框架)'}</b>
                        {copyFromID > 0 && (
                            <span className="ml-2 text-xs text-slate-500">
                                (以 #{copyFromID} 为模板复制)
                            </span>
                        )}
                    </span>
                    <PermissionLink
                        resource="param_row"
                        hide
                        to="/param-sets"
                        className="ml-auto text-sm text-brand-600 hover:underline"
                    >
                        ← 返回参数总览
                    </PermissionLink>
                </div>
            )}

            {/* 只读态整体禁用 (fieldset disabled 传递到所有表单控件) */}
            <fieldset
                disabled={viewMode}
                className="space-y-5 min-w-0 border-0 p-0 m-0"
            >
                {/* 顶部: 标题 + 工艺框架 chips */}
                <div className="card p-4">
                    <div className="text-sm font-semibold mb-1">
                        参数管理 · 动态 Schema 表单
                    </div>
                    <div className="text-xs text-slate-500 mb-3">
                        按工艺框架组织 (一框架一张参数表):
                        框架内设备/瓦数/加工类型差异由行级 fields/params
                        动态吸收.
                        {archIsLocked && (
                            <span className="ml-2 text-amber-600">
                                (编辑模式下不可切换框架)
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-slate-400 mr-1">
                            工艺框架:
                        </span>
                        {archs.map((a) => {
                            const usable = archUsable(a);
                            return (
                                <span
                                    key={a.code}
                                    title={
                                        usable
                                            ? undefined
                                            : '该框架尚未落参数表'
                                    }
                                    className={`tag ${
                                        a.code === archCode
                                            ? 'bg-brand-500 text-white'
                                            : archIsLocked || !usable
                                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                              : 'bg-slate-100 text-slate-600 cursor-pointer hover:bg-slate-200'
                                    }`}
                                    onClick={() => usable && onSetArch(a.code)}
                                >
                                    {a.icon} {a.label}
                                </span>
                            );
                        })}
                    </div>
                </div>

                {/* 关键字段: 材料键 (随框架分化) + 设备 + 加工类型 */}
                <div className="card p-4">
                    <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                        关键字段
                        <span className="tag bg-emerald-50 text-emerald-700">
                            材料键:{' '}
                            {keyKind === 'dual_xcode'
                                ? '膜+基材 (双材料)'
                                : keyKind === 'category_code'
                                  ? '一级 + 二级分类'
                                  : 'xcode'}
                        </span>
                    </div>
                    <div className="grid grid-cols-6 gap-3">
                        {/* 材料键: 三种粒度三种控件 */}
                        {keyKind === 'xcode' && (
                            <div className="col-span-2">
                                <label className="field-label">
                                    <RequiredMark required />
                                    材料
                                </label>
                                <SearchSelect
                                    className="w-full"
                                    value={xcode}
                                    valueLabel={xcodeLabel || xcode}
                                    onChange={(v, opt) => {
                                        setXcode(v);
                                        setXcodeLabel(opt?.label || '');
                                        setDirty(true);
                                    }}
                                    placeholder="搜 xcode / 名称"
                                    loadOptions={loadMaterialOptions}
                                />
                            </div>
                        )}
                        {keyKind === 'dual_xcode' && (
                            <>
                                <div className="col-span-2">
                                    <label className="field-label">
                                        <RequiredMark required />膜 (主材料)
                                    </label>
                                    <SearchSelect
                                        className="w-full"
                                        value={filmXcode}
                                        valueLabel={filmLabel || filmXcode}
                                        onChange={(v, opt) => {
                                            setFilmXcode(v);
                                            setFilmLabel(opt?.label || '');
                                            setDirty(true);
                                        }}
                                        placeholder="搜热压膜 xcode (HPM-*)"
                                        loadOptions={loadMaterialOptions}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="field-label">
                                        基材{' '}
                                        <span className="text-slate-400 text-xs">
                                            (烘烤等单材料加工类型可空)
                                        </span>
                                    </label>
                                    <SearchSelect
                                        className="w-full"
                                        value={substrateXcode}
                                        valueLabel={
                                            substrateLabel || substrateXcode
                                        }
                                        onChange={(v, opt) => {
                                            setSubstrateXcode(v);
                                            setSubstrateLabel(opt?.label || '');
                                            setDirty(true);
                                        }}
                                        placeholder="搜基材 xcode"
                                        loadOptions={loadMaterialOptions}
                                        clearable
                                    />
                                </div>
                            </>
                        )}
                        {keyKind === 'category_code' && (
                            <div className="col-span-2">
                                <label className="field-label">
                                    <RequiredMark required />
                                    材料分类{' '}
                                    <span className="text-slate-400 text-xs">
                                        (不细分耗材)
                                    </span>
                                </label>
                                <SearchSelect
                                    className="w-full"
                                    value={categoryValue}
                                    onChange={(v) => {
                                        const p = decodeCategoryPair(v);
                                        setCategoryCode(p.type2);
                                        setCategoryType1(p.type1);
                                        setDirty(true);
                                    }}
                                    placeholder="请选择 一级 / 二级分类"
                                    options={withCurrent(
                                        catPair.options,
                                        categoryValue,
                                        categoryPairFallbackLabel(categoryValue),
                                    )}
                                />
                            </div>
                        )}
                        <div>
                            <label className="field-label">
                                <RequiredMark required />
                                设备
                            </label>
                            {/* 没有设备查看权时设备清单是空的 (不拉), 但本行的 deviceKey 要照常显示 ——
                                补一条占位项, 否则只读态会显示成"请选择设备" (2026-09-13) */}
                            <SearchSelect
                                className="w-full"
                                value={deviceKey}
                                onChange={(v) => {
                                    setDeviceKey(v);
                                    setDirty(true);
                                }}
                                placeholder="请选择设备"
                                options={withCurrent(deviceOpts, deviceKey)}
                            />
                        </div>
                        {/* 加工模式: 决定模板键的 groupCode 段 (经 mm_mode 字典换算), 是关键字段不是普通参数值.
                        值仍存在 params.processingMode —— 与后端 mmGroupCodeFor 的取值口径一致. */}
                        <div title="加工模式决定命中哪张 Schema 模板, 选完下面的参数值字段会跟着变">
                            <label className="field-label">
                                加工模式
                                {archSchemas.some((s) => !!s.groupCode) && (
                                    <span className="text-slate-400 text-xs ml-1">
                                        (有模板的模式)
                                    </span>
                                )}
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={processingMode}
                                onChange={(v) => setProcessingMode(v)}
                                placeholder="请选择…"
                                options={withCurrent(
                                    modeSelectOpts,
                                    processingMode,
                                    `${processingMode} (不在候选)`,
                                )}
                            />
                            {processingMode && (
                                <label
                                    className="mt-1 flex items-center gap-1 text-xs text-slate-600"
                                    title="勾选后本行只对所选加工模式生效, 同组其它模式仍用共用的那行; 例: 某块打印板不能用 → 该板单独一行, 结果选「不支持」"
                                >
                                    <input
                                        type="checkbox"
                                        checked={modeOnly}
                                        onChange={(e) => {
                                            setModeOnly(e.target.checked);
                                            setDirty(true);
                                        }}
                                    />
                                    仅对此加工模式生效
                                </label>
                            )}
                        </div>
                        <div title="候选=当前 设备+加工模式 下建了模板的加工类型">
                            <label className="field-label">
                                加工类型
                                {subProcessShownOpts.length <
                                    subProcessOpts.length && (
                                    <span className="text-slate-400 text-xs ml-1">
                                        (有模板的类型)
                                    </span>
                                )}
                            </label>
                            {/* 编辑老数据: 行里存的类型不在候选时保留显示, 不丢值 */}
                            <SearchSelect
                                className="w-full"
                                value={subProcess}
                                onChange={(v) => {
                                    setSubProcess(v);
                                    setDirty(true);
                                }}
                                placeholder="请选择"
                                options={withCurrent(
                                    subProcessShownOpts.map((o) => ({
                                        value: o.code,
                                        label: o.label,
                                    })),
                                    subProcess,
                                    `${
                                        subProcessOpts.find(
                                            (o) => o.code === subProcess,
                                        )?.label || subProcess
                                    } (不在候选)`,
                                )}
                            />
                        </div>
                        {/* 方案类型: 选项随工艺架构变 (字典 plan_type 按 arch scope); 值存 params.planType */}
                        <div>
                            <label className="field-label">方案类型</label>
                            <select
                                className="sel"
                                value={planType}
                                onChange={(e) => setPlanType(e.target.value)}
                                title="选项来自 数据字典 · 方案类型, 按工艺架构维护, 并可绑定到具体设备"
                            >
                                <option value="">
                                    {planTypeOpts.length === 0
                                        ? planTypeAll.length === 0
                                            ? '该架构暂无方案类型'
                                            : '该设备暂无方案类型'
                                        : '未设置'}
                                </option>
                                {planTypeOpts.map((o) => (
                                    <option key={o.code} value={o.code}>
                                        {o.label}
                                    </option>
                                ))}
                                {planType !== '' &&
                                    !planTypeOpts.some(
                                        (o) => o.code === planType,
                                    ) && (
                                        <option value={planType}>
                                            {planType}{' '}
                                            {planTypeAll.some((o) => o.code === planType)
                                                ? '(未绑定该设备)'
                                                : '(不在字典)'}
                                        </option>
                                    )}
                            </select>
                        </div>
                        {/* 结果标: 这组参数的实测结论, 与参数值同一次编辑、同一条审批流 */}
                        <div>
                            <label className="field-label">
                                结果
                                <span className="text-slate-400 text-xs ml-1">
                                    (实测结论)
                                </span>
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={resultTag}
                                onChange={(v) => {
                                    setResultTag(v);
                                    setDirty(true);
                                }}
                                placeholder="请选择…"
                                options={RESULT_TAG_OPTS}
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="field-label">状态</label>
                            <div className="inp flex items-center gap-2 bg-slate-50 text-slate-600">
                                <span className="tag bg-slate-200 text-slate-700">
                                    {STATUS_OPTS.find((s) => s.v === status)
                                        ?.label || status}
                                </span>
                                <span className="text-xs text-slate-400">
                                    由审批工作流驱动, 不可手动改
                                </span>
                            </div>
                        </div>
                    </div>
                    {(legacyMaterialId > 0 || sourceTable) && (
                        <div className="text-[11px] text-slate-400 mt-3">
                            迁移溯源 · 旧 material_id {legacyMaterialId || '—'}
                            {sourceTable ? ` · 源表 ${sourceTable}` : ''}
                        </div>
                    )}
                </div>

                {/* 动态参数字段 (fields 驱动) */}
                <div className="card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold flex items-center gap-2">
                            {activeArch?.icon} {activeArch?.label || archCode} ·
                            参数值
                            <PermissionLink
                                resource="param_schema"
                                verb="update"
                                hide
                                to={schemaLink}
                                className="tag bg-amber-50 text-amber-700 hover:underline whitespace-nowrap"
                                title={`字段集来自 Schema 模板${
                                    schemaFrom ? ` (${schemaFrom})` : ''
                                }; 框机/振镜/瓦数差异由模板键分化. 点击去模板管理`}
                            >
                                Schema 模板{schemaFrom ? `: ${schemaFrom}` : ''}{' '}
                                ↗
                            </PermissionLink>
                        </div>
                    </div>

                    {schemaError && (
                        <div className="card p-3 mb-3 text-sm text-amber-700 border-amber-200 bg-amber-50">
                            {schemaError}
                        </div>
                    )}

                    {formFields.length === 0 ? (
                        <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-4 text-xs text-slate-400 py-4 text-center">
                            {viewMode ? (
                                '该参数行暂无字段'
                            ) : (
                                noDefaultTpl && !deviceKey && !subProcess && !processingMode ? (
                                    '本框架的模板按设备 / 加工模式 / 加工类型匹配, 先选好上面的关键字段再录入参数'
                                ) : (
                                <>
                                    当前键未命中任何模板字段, 去&nbsp;
                                    <PermissionLink
                                        resource="param_schema"
                                        to="/param-sets/templates"
                                        className="text-brand-600 hover:underline"
                                    >
                                        📐 Schema模板管理
                                    </PermissionLink>
                                    &nbsp;配置
                                </>
                                )
                            )}
                        </div>
                        </div>
                    ) : (
                        // 模板有分组布局时按 页签 → 分组 嵌套展示, 没有时与原平铺一致
                        <GroupedFields
                            items={linkedFields}
                            attrs={fieldGroupAttrs}
                            layout={layout}
                            itemKey={fieldKeyOf}
                            gridClassName="grid grid-cols-4 gap-4"
                            collapsible
                            renderItem={(f) => (
                                    <div key={f.k}>
                                        <label className="field-label" title={f.k}>
                                            <RequiredMark required={f.required} />
                                            {fieldDisplayName(f.k, fieldNames)}
                                            {f.u ? (
                                                <span className="text-slate-400 text-xs ml-1">
                                                    ({f.u})
                                                </span>
                                            ) : null}
                                            {extraFieldKeys.includes(f.k) && (
                                                <span
                                                    className="tag bg-slate-100 text-slate-500 ml-1"
                                                    title="模板已不含该字段, 但本行存有值 — 保留展示以便清理/修正"
                                                >
                                                    非模板
                                                </span>
                                            )}
                                        </label>
                                        <SchemaInput
                                            f={f}
                                            value={params[f.k]}
                                            mmodes={mmodes}
                                            dictOpts={dictOpts}
                                            onChange={(v) => {
                                                setParams({ ...params, [f.k]: v });
                                                setDirty(true);
                                            }}
                                        />
                                    </div>
                            )}
                        />
                    )}
                    {linkHiddenCnt > 0 && (
                        <div className="text-[11px] text-slate-300 mt-1">
                            另有 {linkHiddenCnt} 个字段因关联开关未开启 /
                            模式不匹配而隐藏, 打开对应开关后显示
                        </div>
                    )}

                    {/* 效果图 / 矩阵整图 (2026-07-30 起内嵌参数行, 与源 material_process_para 同名同义)
                    两类图片分块展示: 效果图 = effect_image; 矩阵整图 = para_matrix.frontImg
                    (仅矩阵行有, 行列/坐标数据只读, 整图可替换). */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                        <div
                            className={`grid grid-cols-1 gap-4 ${supportMatrix ? 'md:grid-cols-2' : ''}`}
                        >
                            {/* ---- 效果图 (effect_image) ---- */}
                            <div>
                                <div className="text-xs font-semibold text-slate-600 mb-2">
                                    效果图
                                </div>
                                {!viewMode && !formLocked ? (
                                    <div className="flex items-start gap-3">
                                        {/* 上传控件兼预览 (直传 R2 后回填 CDN 地址; 已有图可替换/清除) */}
                                        <ImageUploader
                                            value={effectImage.trim()}
                                            onChange={(url) => {
                                                setEffectImage(url);
                                                setDirty(true);
                                            }}
                                            placeholder="上传效果图"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <input
                                                className="inp"
                                                placeholder="或粘贴图片外链 (https://…)"
                                                value={effectImage}
                                                onChange={(e) => {
                                                    setEffectImage(
                                                        e.target.value,
                                                    );
                                                    setDirty(true);
                                                }}
                                            />
                                            <div className="text-[11px] text-slate-400 mt-1">
                                                上传或粘外链均可, 留空 =
                                                无效果图; 保存草稿后生效
                                            </div>
                                        </div>
                                    </div>
                                ) : effectImage.trim() ? (
                                    <a
                                        href={imgSrc(effectImage.trim())}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="点击看大图"
                                    >
                                        <img
                                            src={thumbUrl(
                                                imgSrc(effectImage.trim()),
                                            )}
                                            alt="效果图预览"
                                            loading="lazy"
                                            decoding="async"
                                            className="h-24 rounded border border-slate-200 object-cover hover:ring-2 hover:ring-brand-300"
                                        />
                                    </a>
                                ) : (
                                    <span className="text-xs text-slate-400">
                                        无效果图
                                    </span>
                                )}
                            </div>

                            {/* ---- 矩阵整图 (para_matrix.frontImg) ---- */}
                            {supportMatrix && matrixMeta && (
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-semibold text-slate-600">
                                            矩阵整图
                                        </span>
                                        <span className="tag bg-violet-50 text-violet-700">
                                            矩阵
                                        </span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        {!viewMode && !formLocked ? (
                                            <ImageUploader
                                                value={matrixMeta.frontImg}
                                                onChange={setMatrixFrontImg}
                                                placeholder="上传矩阵图"
                                            />
                                        ) : matrixMeta.frontImg ? (
                                            <a
                                                href={matrixMeta.frontImg}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="矩阵整图 (点击看大图)"
                                            >
                                                <img
                                                    src={thumbUrl(
                                                        matrixMeta.frontImg,
                                                    )}
                                                    alt="矩阵图"
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="h-24 rounded border border-slate-200 object-cover hover:ring-2 hover:ring-violet-300"
                                                />
                                            </a>
                                        ) : (
                                            <span className="text-xs text-slate-400">
                                                无矩阵图
                                            </span>
                                        )}
                                        <div className="text-xs text-slate-500 space-y-0.5 min-w-0">
                                            <div>
                                                {matrixMeta.row ?? '?'} 行 ×{' '}
                                                {matrixMeta.col ?? '?'} 列
                                                {matrixMeta.matrixPoint && (
                                                    <span className="text-slate-400">
                                                        {' '}
                                                        ·{' '}
                                                        {
                                                            matrixMeta
                                                                .matrixPoint
                                                                .length
                                                        }{' '}
                                                        格
                                                    </span>
                                                )}
                                            </div>
                                            {(matrixMeta.xType ||
                                                matrixMeta.yType) && (
                                                <div>
                                                    轴:{' '}
                                                    {String(
                                                        matrixMeta.xType || '?',
                                                    )}{' '}
                                                    ×{' '}
                                                    {String(
                                                        matrixMeta.yType || '?',
                                                    )}
                                                    {matrixMeta.minPower !==
                                                        undefined && (
                                                        <span>
                                                            {' '}
                                                            · 功率{' '}
                                                            {String(
                                                                matrixMeta.minPower,
                                                            )}
                                                            –
                                                            {String(
                                                                matrixMeta.maxPower,
                                                            )}
                                                        </span>
                                                    )}
                                                    {matrixMeta.minSpeed !==
                                                        undefined && (
                                                        <span>
                                                            {' '}
                                                            · 速度{' '}
                                                            {String(
                                                                matrixMeta.minSpeed,
                                                            )}
                                                            –
                                                            {String(
                                                                matrixMeta.maxSpeed,
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            <div className="text-[11px] text-slate-400">
                                                行列/每格坐标由同步链路带入只读;
                                                整图可上传替换, 保存草稿后生效
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {pageError && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {pageError}
                    </div>
                )}

                {/* 底部按钮 */}
                {formLocked && (
                    <div className="card p-3 text-sm text-amber-700 border-amber-200 bg-amber-50">
                        {statusLockReason}
                    </div>
                )}
                <div className="flex justify-end gap-2 items-center">
                    {dirty && (
                        <span className="text-xs text-amber-700">
                            ● 有未保存改动, 请点保存草稿
                        </span>
                    )}
                    <button
                        type="button"
                        className={`btn-ghost ${dirty ? 'border-amber-400 bg-amber-50 text-amber-700' : ''}`}
                        onClick={onSaveDraft}
                        hidden={!canUpdate}
                        disabled={busy || formLocked}
                        title={formLocked ? statusLockReason : undefined}
                    >
                        {busy ? '保存中…' : isEditing ? '保存修改' : '保存草稿'}
                    </button>
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onSaveAndCopy}
                        disabled={busy || formLocked}
                        title="保存本行, 然后以它为模板继续录下一条 (材料/设备/字段带过去, 只改数值)"
                        hidden={!canUpdate}
                    >
                        保存并复制新建
                    </button>
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onValidateRange}
                        disabled={busy || !isEditing}
                        title={!isEditing ? '请先保存草稿后再校验' : undefined}
                    >
                        校验工况范围
                    </button>
                    {isEditing && status === 'reviewing' ? (
                        <button
                            type="button"
                            className="btn-ghost text-amber-700 border-amber-200 hover:bg-amber-50"
                            onClick={onWithdraw}
                            disabled={busy}
                            hidden={!canUpdate}
                        >
                            撤回评审
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={onSubmitReview}
                            disabled={busy}
                            title={
                                status === 'published'
                                    ? '修改会先回落 draft, 再提交评审'
                                    : !isEditing
                                      ? '新建后会先入库再提交评审'
                                      : undefined
                            }
                            hidden={!canUpdate}
                        >
                            提交评审
                        </button>
                    )}
                </div>
            </fieldset>
        </div>
    );
}

export default ParamManage;
